// Spanish translations for common UI elements and medical content
// This provides instant translations without API calls

export const uiTranslations = {
  en: {
    // Navigation & Common UI
    home: 'Home',
    backToLibrary: 'Back to Library',
    readMore: 'Read More',
    viewAll: 'View All',
    loading: 'Loading...',
    notFound: 'Not Found',
    search: 'Search',
    
    // Article UI
    minRead: 'min read',
    author: 'Author',
    publishedOn: 'Published on',
    lastUpdated: 'Last updated',
    keyPoints: 'Key Points',
    relatedTopics: 'Related Topics',
    relatedVideos: 'Related Videos',
    
    // Medical Warnings
    whenToCallDoctor: 'When to Call Your Doctor',
    whenToCallDoctorImmediately: 'When to Call Your Doctor Immediately',
    seekImmediateCare: 'Seek immediate care for severe pain, high fever, inability to urinate, or heavy bleeding.',
    emergencyWarning: 'EMERGENCY: Call 911 or Go to ER Immediately',
    urgentWarning: 'URGENT: Contact Your Urologist Within 24-72 Hours',
    
    // Disclaimers
    medicalDisclaimer: 'Medical Disclaimer',
    disclaimerText: 'This content is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment.',
    importantDisclaimer: 'Important: This content is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment.',
    
    // After Visit
    afterVisitCare: 'After Visit Care',
    postProcedureInstructions: 'Post-Procedure Instructions',
    reasonForVisit: 'Reason for Visit',
    whatToExpect: 'What to Expect',
    activityGuidelines: 'Activity Guidelines',
    normalSymptoms: 'Normal Recovery Symptoms',
    printInstructions: 'Print Instructions',
    
    // Sections
    overview: 'Overview',
    symptoms: 'Symptoms',
    causes: 'Causes',
    diagnosis: 'Diagnosis',
    treatment: 'Treatment',
    prevention: 'Prevention',
    recovery: 'Recovery',
    followUp: 'Follow-Up',
    
    // Actions
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    showAll: 'Show All',
    
    // Language Toggle
    viewInSpanish: 'Ver en Español',
    viewInEnglish: 'View in English'
  },
  es: {
    // Navigation & Common UI
    home: 'Inicio',
    backToLibrary: 'Volver a la Biblioteca',
    readMore: 'Leer Más',
    viewAll: 'Ver Todos',
    loading: 'Cargando...',
    notFound: 'No Encontrado',
    search: 'Buscar',
    
    // Article UI
    minRead: 'min de lectura',
    author: 'Autor',
    publishedOn: 'Publicado el',
    lastUpdated: 'Última actualización',
    keyPoints: 'Puntos Clave',
    relatedTopics: 'Temas Relacionados',
    relatedVideos: 'Videos Relacionados',
    
    // Medical Warnings
    whenToCallDoctor: 'Cuándo Llamar a Su Médico',
    whenToCallDoctorImmediately: 'Cuándo Llamar a Su Médico Inmediatamente',
    seekImmediateCare: 'Busque atención inmediata por dolor severo, fiebre alta, incapacidad para orinar o sangrado abundante.',
    emergencyWarning: 'EMERGENCIA: Llame al 911 o Vaya a la Sala de Emergencias Inmediatamente',
    urgentWarning: 'URGENTE: Contacte a Su Urólogo Dentro de 24-72 Horas',
    
    // Disclaimers
    medicalDisclaimer: 'Aviso Médico',
    disclaimerText: 'Este contenido es solo para fines educativos y no sustituye el consejo, diagnóstico o tratamiento médico profesional.',
    importantDisclaimer: 'Importante: Este contenido es solo para fines educativos y no sustituye el consejo, diagnóstico o tratamiento médico profesional.',
    
    // After Visit
    afterVisitCare: 'Cuidados Después de la Visita',
    postProcedureInstructions: 'Instrucciones Post-Procedimiento',
    reasonForVisit: 'Razón de la Visita',
    whatToExpect: 'Qué Esperar',
    activityGuidelines: 'Guías de Actividad',
    normalSymptoms: 'Síntomas Normales de Recuperación',
    printInstructions: 'Imprimir Instrucciones',
    
    // Sections
    overview: 'Descripción General',
    symptoms: 'Síntomas',
    causes: 'Causas',
    diagnosis: 'Diagnóstico',
    treatment: 'Tratamiento',
    prevention: 'Prevención',
    recovery: 'Recuperación',
    followUp: 'Seguimiento',
    
    // Actions
    expandAll: 'Expandir Todo',
    collapseAll: 'Contraer Todo',
    showAll: 'Mostrar Todo',
    
    // Language Toggle
    viewInSpanish: 'Ver en Español',
    viewInEnglish: 'View in English'
  }
};

// Common medical term translations
export const medicalTerms = {
  // Conditions
  'kidney stone': 'cálculo renal',
  'kidney stones': 'cálculos renales',
  'bladder cancer': 'cáncer de vejiga',
  'prostate cancer': 'cáncer de próstata',
  'kidney cancer': 'cáncer de riñón',
  'testicular cancer': 'cáncer testicular',
  'BPH': 'HPB (Hiperplasia Prostática Benigna)',
  'enlarged prostate': 'próstata agrandada',
  'UTI': 'infección del tracto urinario',
  'urinary tract infection': 'infección del tracto urinario',
  'incontinence': 'incontinencia',
  'erectile dysfunction': 'disfunción eréctil',
  'hematuria': 'hematuria (sangre en la orina)',
  'blood in urine': 'sangre en la orina',
  
  // Procedures
  'cystoscopy': 'cistoscopia',
  'biopsy': 'biopsia',
  'TURP': 'RTUP (Resección Transuretral de Próstata)',
  'prostatectomy': 'prostatectomía',
  'nephrectomy': 'nefrectomía',
  'orchiectomy': 'orquiectomía',
  'vasectomy': 'vasectomía',
  'circumcision': 'circuncisión',
  'lithotripsy': 'litotricia',
  'ureteroscopy': 'ureteroscopia',
  'catheter': 'catéter',
  'stent': 'stent',
  
  // Anatomy
  'kidney': 'riñón',
  'kidneys': 'riñones',
  'bladder': 'vejiga',
  'prostate': 'próstata',
  'urethra': 'uretra',
  'ureter': 'uréter',
  'testicle': 'testículo',
  'testicles': 'testículos',
  'scrotum': 'escroto',
  'penis': 'pene',
  
  // Symptoms
  'pain': 'dolor',
  'burning': 'ardor',
  'frequency': 'frecuencia',
  'urgency': 'urgencia',
  'fever': 'fiebre',
  'bleeding': 'sangrado',
  'swelling': 'hinchazón',
  'discharge': 'secreción'
};

// Function to get UI translation
export const t = (key, language = 'en') => {
  return uiTranslations[language]?.[key] || uiTranslations.en[key] || key;
};

// Simple content translation using common patterns
export const translateContent = (content, targetLang = 'es') => {
  if (targetLang === 'en' || !content) return content;
  
  let translated = content;
  
  // Replace common medical terms
  Object.entries(medicalTerms).forEach(([en, es]) => {
    const regex = new RegExp(`\\b${en}\\b`, 'gi');
    translated = translated.replace(regex, es);
  });
  
  return translated;
};

// Translate section headings commonly found in medical articles
export const translateHeading = (heading, language = 'en') => {
  if (language === 'en') return heading;
  
  const headingTranslations = {
    'Overview': 'Descripción General',
    'What is': 'Qué es',
    'What are': 'Qué son',
    'Symptoms': 'Síntomas',
    'Causes': 'Causas',
    'Risk Factors': 'Factores de Riesgo',
    'Diagnosis': 'Diagnóstico',
    'Treatment': 'Tratamiento',
    'Treatment Options': 'Opciones de Tratamiento',
    'Prevention': 'Prevención',
    'Recovery': 'Recuperación',
    'Prognosis': 'Pronóstico',
    'Complications': 'Complicaciones',
    'When to See a Doctor': 'Cuándo Ver a un Médico',
    'When to Seek Care': 'Cuándo Buscar Atención',
    'Key Takeaways': 'Puntos Clave',
    'Summary': 'Resumen',
    'Before the Procedure': 'Antes del Procedimiento',
    'After the Procedure': 'Después del Procedimiento',
    'During the Procedure': 'Durante el Procedimiento',
    'What to Expect': 'Qué Esperar',
    'How It Works': 'Cómo Funciona',
    'Benefits': 'Beneficios',
    'Risks': 'Riesgos',
    'Side Effects': 'Efectos Secundarios',
    'Follow-Up': 'Seguimiento',
    'Follow-Up Care': 'Cuidado de Seguimiento',
    'Lifestyle Changes': 'Cambios en el Estilo de Vida',
    'Medications': 'Medicamentos',
    'Surgery': 'Cirugía',
    'Types': 'Tipos',
    'Stages': 'Etapas',
    'Statistics': 'Estadísticas',
    'FAQ': 'Preguntas Frecuentes',
    'References': 'Referencias',
    'Contact Your Doctor If': 'Contacte a Su Médico Si',
    'Emergency Signs': 'Señales de Emergencia',
    'Normal vs Abnormal': 'Normal vs Anormal',
    'Activity Guidelines': 'Guías de Actividad',
    'Diet Recommendations': 'Recomendaciones Dietéticas'
  };
  
  // Check for exact match first
  if (headingTranslations[heading]) {
    return headingTranslations[heading];
  }
  
  // Check for partial matches
  for (const [en, es] of Object.entries(headingTranslations)) {
    if (heading.toLowerCase().includes(en.toLowerCase())) {
      return heading.replace(new RegExp(en, 'gi'), es);
    }
  }
  
  return heading;
};

export default { t, translateContent, translateHeading, uiTranslations, medicalTerms };
